import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { ArrowRight, ArrowLeft, Check, CheckCircle2, Loader2, MessageCircle, XCircle, Upload, ImagePlus, X as XIcon } from "lucide-react";
import { applianceMeta, timeSlots, businessInfo } from "../constants";
import { getServices, checkPincode, createBooking, getPrices, getPaymentConfig, createPaymentOrder, verifyPayment } from "../api";
import { useToast } from "../hooks/use-toast";

const steps = ["Appliance", "Service", "PIN Code", "Customer", "Schedule", "Photos", "Confirm"];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function BookService() {
  const [params] = useSearchParams();
  const { toast } = useToast();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [servicesForApp, setServicesForApp] = useState([]);
  const [prices, setPrices] = useState([]);
  const [pinState, setPinState] = useState({ checked: false, ok: false, msg: "" });
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [paymentCfg, setPaymentCfg] = useState({ enabled: false, key_id: "", mode: "test" });

  const [form, setForm] = useState({
    appliance: params.get("appliance") || "",
    service: params.get("service") || "",
    pincode: params.get("pin") || "",
    address: "",
    customer_name: "",
    mobile: "",
    email: "",
    preferred_date: "",
    preferred_slot: "",
    problem_description: "",
    photos: [],
    payment_option: "pay_later",
  });

  useEffect(() => { getPrices().then(setPrices).catch(() => {}); getPaymentConfig().then(setPaymentCfg).catch(() => {}); }, []);
  useEffect(() => { if (form.appliance) getServices(form.appliance).then(setServicesForApp).catch(() => {}); }, [form.appliance]);

  useEffect(() => {
    if (form.pincode && form.pincode.length === 6 && !pinState.checked) {
      checkPincode(form.pincode).then((r) => setPinState({ checked: true, ok: r.serviceable, msg: r.message }));
    }
    if (params.get("appliance") && step === 0) setStep(params.get("service") ? 2 : 1);
    // eslint-disable-next-line
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const estimateCharge = useMemo(() => {
    const a = form.appliance;
    const map = {
      chimney: form.service?.toLowerCase().includes("deep") ? "Deep Chimney Cleaning" : form.service?.toLowerCase().includes("clean") ? "Chimney Cleaning" : form.service?.toLowerCase().includes("install") ? "Installation" : "Inspection / Visit",
      hob: form.service?.toLowerCase().includes("install") ? "Installation" : "Hob Service",
      cooktop: form.service?.toLowerCase().includes("install") ? "Installation" : "Cooktop Service",
      oven: form.service?.toLowerCase().includes("install") ? "Installation" : "Built-in Oven Service",
      microwave: form.service?.toLowerCase().includes("install") ? "Installation" : "Microwave Oven Service",
      dishwasher: form.service?.toLowerCase().includes("install") ? "Installation" : "Dishwasher Service",
    };
    const label = map[a] || "Inspection / Visit";
    return prices.find((p) => p.label === label) || prices.find((p) => p.label === "Inspection / Visit");
  }, [form.appliance, form.service, prices]);

  // Parse amount from starting_price string like "₹1,000 + GST"
  const estimateAmount = useMemo(() => {
    if (!estimateCharge) return 0;
    const raw = String(estimateCharge.starting_price).replace(/[^0-9]/g, "");
    const base = parseInt(raw || "0", 10);
    if (!base) return 0;
    const withGst = Math.round(base * 1.18);
    return withGst;
  }, [estimateCharge]);

  const verifyPin = async () => {
    if (!/^[0-9]{6}$/.test(form.pincode)) { setPinState({ checked: true, ok: false, msg: "Please enter a valid 6-digit PIN code." }); return false; }
    setLoading(true);
    try {
      const r = await checkPincode(form.pincode);
      setPinState({ checked: true, ok: r.serviceable, msg: r.message });
      return r.serviceable;
    } finally { setLoading(false); }
  };

  const canNext = () => {
    if (step === 0) return !!form.appliance;
    if (step === 1) return !!form.service;
    if (step === 2) return pinState.checked && pinState.ok && !!form.address.trim();
    if (step === 3) return form.customer_name.trim() && /^[0-9]{10}$/.test(form.mobile);
    if (step === 4) return !!form.preferred_date && !!form.preferred_slot;
    if (step === 5) return true;
    return true;
  };

  const goNext = async () => {
    if (step === 2 && !pinState.ok) {
      const ok = await verifyPin();
      if (!ok) return;
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - form.photos.length);
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { toast({ title: `${f.name} is over 5MB`, description: "Please pick a smaller image." }); continue; }
      const b64 = await fileToBase64(f);
      setForm((prev) => ({ ...prev, photos: [...prev.photos, b64].slice(0, 3) }));
    }
    e.target.value = "";
  };

  const removePhoto = (idx) => setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));

  const submitPayLater = async () => {
    setLoading(true);
    try {
      const r = await createBooking({ ...form, service_charge: estimateAmount });
      setConfirmation({ ...r, price: estimateCharge, amount: estimateAmount, paid: false });
      autoOpenWhatsApp(r.job_no);
      toast({ title: "Booking confirmed!", description: `Job number: ${r.job_no}` });
    } catch (e) {
      toast({ title: "Could not create booking", description: e?.response?.data?.detail || "Please try again." });
    } finally { setLoading(false); }
  };

  const submitPayOnline = async () => {
    setLoading(true);
    try {
      const bk = await createBooking({ ...form, service_charge: estimateAmount, payment_option: "pay_online" });
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay");
      const order = await createPaymentOrder({ booking_id: bk.booking_id, amount: estimateAmount });
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Revive Solutions",
        description: `Elica ${applianceMeta[form.appliance]?.title} – ${form.service}`,
        order_id: order.order_id,
        prefill: { name: form.customer_name, contact: form.mobile, email: form.email },
        theme: { color: "#e30613" },
        handler: async (resp) => {
          try {
            await verifyPayment({ booking_id: bk.booking_id, ...resp });
            setConfirmation({ ...bk, price: estimateCharge, amount: estimateAmount, paid: true, txn: resp.razorpay_payment_id });
            autoOpenWhatsApp(bk.job_no);
            toast({ title: "Payment received", description: `Job: ${bk.job_no}` });
          } catch (e) {
            toast({ title: "Payment verification failed", description: e?.response?.data?.detail || "Please contact us." });
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast({ title: "Payment could not start", description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  const autoOpenWhatsApp = (jobNo) => {
    const text = encodeURIComponent(
      `Hello Revive Solutions,\n\nI just booked a service.\n\nJob: ${jobNo}\nName: ${form.customer_name}\nMobile: +91 ${form.mobile}\nAppliance: Elica ${applianceMeta[form.appliance]?.title}\nService: ${form.service}\nPIN: ${form.pincode}\nDate: ${form.preferred_date} (${form.preferred_slot})\n\nPlease confirm.`
    );
    setTimeout(() => window.open(`${businessInfo.whatsapp}?text=${text}`, "_blank"), 400);
  };

  const submit = () => (form.payment_option === "pay_online" && paymentCfg.enabled ? submitPayOnline() : submitPayLater());

  if (confirmation) return <Confirmation confirmation={confirmation} form={form} />;

  return (
    <Layout>
      <SEO title="Book Elica Service in Pune | Revive Solutions" description="Book Elica chimney, hob, cooktop, oven, microwave or dishwasher service. Check PIN serviceability and pick your preferred slot." canonical="/book-service" />

      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-8">
            <div className="chip mb-3">Book a Service</div>
            <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight">Book your Elica service in a minute</h1>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto no-scrollbar pb-2">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-shrink-0">
                <div className={`step-dot ${i <= step ? "bg-[#e30613] text-white" : "bg-neutral-200 text-slate-500"}`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</div>
                <div className={`text-sm font-medium ${i <= step ? "text-[#0f172a]" : "text-slate-400"}`}>{s}</div>
                {i < steps.length - 1 && <div className={`w-4 h-px ${i < step ? "bg-[#e30613]" : "bg-neutral-300"}`} />}
              </div>
            ))}
          </div>

          <div className="bg-white border border-neutral-200 rounded-lg p-6 md:p-8">
            {step === 0 && (
              <div>
                <h2 className="font-display text-2xl mb-1">Which appliance needs service?</h2>
                <p className="text-slate-500 text-sm mb-6">Choose from our supported Elica appliances.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.values(applianceMeta).map((a) => (
                    <button key={a.slug} onClick={() => setField("appliance", a.slug)} className={`text-left p-4 rounded-lg border transition-all ${form.appliance === a.slug ? "border-[#e30613] ring-2 ring-[#e30613]/20" : "border-neutral-200 hover:border-neutral-400"}`}>
                      <div className="aspect-video rounded overflow-hidden bg-neutral-100 mb-3"><img src={a.image} alt={a.title} className="w-full h-full object-cover" loading="lazy" /></div>
                      <div className="font-semibold text-sm">Elica {a.title}</div>
                      <div className="text-xs text-slate-500">{a.tagline}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="font-display text-2xl mb-1">What&apos;s the issue?</h2>
                <p className="text-slate-500 text-sm mb-6">Pick the closest match. You can add details later.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {servicesForApp.map((s) => (
                    <button key={s.id} onClick={() => setField("service", s.title)} className={`text-left p-4 rounded-lg border transition-all ${form.service === s.title ? "border-[#e30613] ring-2 ring-[#e30613]/20" : "border-neutral-200 hover:border-neutral-400"}`}>
                      <div className="font-semibold text-sm">{s.title}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="font-display text-2xl mb-1">Where do we visit?</h2>
                <p className="text-slate-500 text-sm mb-6">Enter your PIN code first. We service select Pune PIN codes only.</p>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="field-label">PIN Code*</label>
                    <div className="flex gap-2">
                      <input value={form.pincode} onChange={(e) => { setField("pincode", e.target.value.replace(/[^0-9]/g, "").slice(0, 6)); setPinState({ checked: false, ok: false, msg: "" }); }} className="field flex-1" placeholder="6-digit PIN" inputMode="numeric" />
                      <button onClick={verifyPin} disabled={loading} className="btn-primary">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check"}</button>
                    </div>
                    {pinState.checked && (
                      <div className={`mt-2 text-sm flex items-center gap-1.5 ${pinState.ok ? "text-green-700" : "text-[#e30613]"}`}>
                        {pinState.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        {pinState.msg}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="field-label">Service Address*</label>
                  <textarea value={form.address} onChange={(e) => setField("address", e.target.value)} rows="3" className="field resize-none" placeholder="Flat / Society / Landmark / Area"></textarea>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="font-display text-2xl mb-1">Your contact details</h2>
                <p className="text-slate-500 text-sm mb-6">So we can confirm your slot and share the technician&apos;s ETA.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="field-label">Full name*</label><input value={form.customer_name} onChange={(e) => setField("customer_name", e.target.value)} className="field" placeholder="Your name" /></div>
                  <div><label className="field-label">Mobile*</label><input value={form.mobile} onChange={(e) => setField("mobile", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))} className="field" placeholder="10-digit mobile" inputMode="numeric" /></div>
                  <div className="md:col-span-2"><label className="field-label">Email (optional)</label><input value={form.email} onChange={(e) => setField("email", e.target.value)} type="email" className="field" placeholder="you@email.com" /></div>
                  <div className="md:col-span-2"><label className="field-label">Additional problem description</label><textarea value={form.problem_description} onChange={(e) => setField("problem_description", e.target.value)} rows="3" className="field resize-none" placeholder="Anything specific we should know?"></textarea></div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="font-display text-2xl mb-1">Pick a date &amp; time slot</h2>
                <p className="text-slate-500 text-sm mb-6">We&apos;ll do our best to accommodate your preferred slot.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="field-label">Preferred date*</label><input type="date" min={new Date().toISOString().slice(0, 10)} value={form.preferred_date} onChange={(e) => setField("preferred_date", e.target.value)} className="field" /></div>
                  <div>
                    <label className="field-label">Preferred slot*</label>
                    <select value={form.preferred_slot} onChange={(e) => setField("preferred_slot", e.target.value)} className="field"><option value="">Select a slot</option>{timeSlots.map((t) => <option key={t} value={t}>{t}</option>)}</select>
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div>
                <h2 className="font-display text-2xl mb-1">Add photos (optional)</h2>
                <p className="text-slate-500 text-sm mb-6">Up to 3 photos of the appliance / issue. This helps our technician arrive prepared. Max 5MB each.</p>
                <div className="grid grid-cols-3 gap-3">
                  {form.photos.map((src, i) => (
                    <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-neutral-200">
                      <img src={src} alt={`photo ${i+1}`} className="w-full h-full object-cover" />
                      <button onClick={() => removePhoto(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white grid place-items-center"><XIcon className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {form.photos.length < 3 && (
                    <label className="aspect-square rounded-lg border-2 border-dashed border-neutral-300 grid place-items-center cursor-pointer hover:border-[#e30613] transition-colors">
                      <div className="text-center"><ImagePlus className="w-6 h-6 text-slate-400 mx-auto mb-1" /><div className="text-xs text-slate-500">Add photo</div></div>
                      <input type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
                    </label>
                  )}
                </div>
              </div>
            )}

            {step === 6 && (
              <div>
                <h2 className="font-display text-2xl mb-1">Review &amp; confirm</h2>
                <p className="text-slate-500 text-sm mb-6">Please review the details below before confirming.</p>
                <div className="grid md:grid-cols-2 gap-4 bg-neutral-50 border border-neutral-200 rounded-lg p-5 mb-5">
                  <Field label="Appliance" value={`Elica ${applianceMeta[form.appliance]?.title}`} />
                  <Field label="Service" value={form.service} />
                  <Field label="PIN Code" value={form.pincode} />
                  <Field label="Address" value={form.address} />
                  <Field label="Name" value={form.customer_name} />
                  <Field label="Mobile" value={`+91 ${form.mobile}`} />
                  <Field label="Date" value={form.preferred_date} />
                  <Field label="Slot" value={form.preferred_slot} />
                  {form.photos.length > 0 && (
                    <div className="md:col-span-2">
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Photos</div>
                      <div className="flex gap-2">{form.photos.map((src, i) => <img key={i} src={src} alt={`p${i}`} className="w-14 h-14 object-cover rounded border border-neutral-200" />)}</div>
                    </div>
                  )}
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-5 mb-5">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Estimated service charge</div>
                  <div className="font-display text-2xl">{estimateAmount ? `₹${estimateAmount.toLocaleString("en-IN")}` : (estimateCharge?.starting_price || "To be assessed")}</div>
                  <div className="text-xs text-slate-500 mt-1">Includes 18% GST. Spare parts, if required, are charged separately after your approval.</div>
                </div>
                <div className="mb-2">
                  <label className="field-label">Payment</label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className={`p-4 rounded-lg border cursor-pointer ${form.payment_option === "pay_later" ? "border-[#e30613] ring-2 ring-[#e30613]/20" : "border-neutral-200"}`}>
                      <input type="radio" className="hidden" checked={form.payment_option === "pay_later"} onChange={() => setField("payment_option", "pay_later")} />
                      <div className="font-semibold text-sm">Pay Later</div>
                      <div className="text-xs text-slate-500">Pay the technician after service completion (Cash / UPI).</div>
                    </label>
                    {paymentCfg.enabled ? (
                      <label className={`p-4 rounded-lg border cursor-pointer ${form.payment_option === "pay_online" ? "border-[#e30613] ring-2 ring-[#e30613]/20" : "border-neutral-200"}`}>
                        <input type="radio" className="hidden" checked={form.payment_option === "pay_online"} onChange={() => setField("payment_option", "pay_online")} />
                        <div className="font-semibold text-sm flex items-center gap-2">Pay Online {paymentCfg.mode === "test" && <span className="badge-red">Test mode</span>}</div>
                        <div className="text-xs text-slate-500">Securely pay via UPI / cards / net-banking via Razorpay.</div>
                      </label>
                    ) : (
                      <label className="p-4 rounded-lg border border-neutral-200 opacity-60 cursor-not-allowed">
                        <div className="font-semibold text-sm flex items-center gap-2">Pay Online <span className="badge-red">Coming soon</span></div>
                        <div className="text-xs text-slate-500">UPI, cards &amp; net-banking will be available shortly.</div>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-neutral-200">
              <button onClick={goBack} disabled={step === 0} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 disabled:opacity-40"><ArrowLeft className="w-4 h-4" /> Back</button>
              {step < steps.length - 1 ? (
                <button onClick={goNext} disabled={!canNext() || loading} className="btn-primary">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}</button>
              ) : (
                <button onClick={submit} disabled={loading} className="btn-primary">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (form.payment_option === "pay_online" && paymentCfg.enabled ? <>Pay &amp; Confirm <ArrowRight className="w-4 h-4" /></> : <>Confirm Booking <Check className="w-4 h-4" /></>)}</button>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, value }) {
  return (
    <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">{label}</div><div className="font-semibold text-sm break-words">{value || "—"}</div></div>
  );
}

function Confirmation({ confirmation, form }) {
  return (
    <Layout>
      <SEO title="Booking Confirmed | Revive Solutions" description="Your Elica service booking has been received. We will contact you shortly." />
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 grid place-items-center mx-auto mb-6"><CheckCircle2 className="w-9 h-9 text-green-600" /></div>
          <div className="chip mb-3">{confirmation.paid ? "Payment Received" : "Booking Confirmed"}</div>
          <h1 className="font-display text-3xl md:text-5xl font-medium mb-3">Thank you, {form.customer_name}!</h1>
          <p className="text-slate-500 mb-8">Your Elica {applianceMeta[form.appliance]?.title} service booking has been received. Our team will call you shortly to confirm.</p>
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6 text-left mb-8">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Job Number</div><div className="font-semibold">{confirmation.job_no}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Appliance</div><div className="font-semibold">Elica {applianceMeta[form.appliance]?.title}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Service</div><div className="font-semibold">{form.service}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Estimated Charge</div><div className="font-semibold">{confirmation.amount ? `₹${confirmation.amount.toLocaleString("en-IN")}` : (confirmation.price?.starting_price || "To be assessed")}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Date</div><div className="font-semibold">{form.preferred_date}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Slot</div><div className="font-semibold">{form.preferred_slot}</div></div>
              {confirmation.paid && (<div className="col-span-2"><div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Payment ID</div><div className="font-mono text-sm">{confirmation.txn}</div></div>)}
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-4">A WhatsApp confirmation has opened in a new tab – tap send to notify our team instantly.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={`${businessInfo.whatsapp}?text=Hi,%20I%20just%20booked%20service%20${confirmation.job_no}.%20Please%20confirm.`} target="_blank" rel="noreferrer" className="btn-primary"><MessageCircle className="w-4 h-4" /> WhatsApp Us Again</a>
            <Link to="/" className="btn-ghost">Back to Home</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
